import { showError, showConfirm } from "@/assets/js/utils/alertService";

export default {
  name: "FormatView",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    initialData: {
      type: Object,
      default: () => null,
    },
    // ✅ 新增：是否為新增商品流程
    isNewProductFlow: {
      type: Boolean,
      default: false,
    },
    // ✅ 新增：用戶角色（用於判斷是否顯示成本價格）
    userRole: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      specType: "single", // 'single' 或 'multiple'
      singleSpec: {
        productCode: "",
        price: null,
        stock: null,
      },
      specifications: [
        {
          productCode: "",
          name: "",
          price: null,
          stock: null,
        },
      ],
      // 暫存多規格資料，用於切換規格類型時保留資料
      tempSpecifications: [],
      // 記錄選中的規格索引
      selectedSpecs: [],
      hasChanges: false,
      isBackAction: false, // ✅ 新增：標記是否為「上一步」操作
    };
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        // ✅ 重新打開時，如果是「上一步」操作，不重新初始化資料
        if (!this.isBackAction) {
          this.initializeData(); // 【控制：打開時回填】
        }
        // ✅ 重置標記
        this.isBackAction = false;
      } else {
        // ✅ 關閉時，如果是「上一步」操作，不清空資料
        if (!this.isBackAction) {
          this.resetData(); // 【控制：關閉時清空】
        }
      }
    },
    // ⭐⭐⭐ 新增這段 ⭐⭐⭐
    async specType(newVal, oldVal) {
      // 初始化或程式內設定，不處理
      if (!oldVal || this.isSwitchingSpecType) return;

      // 判斷是否已有資料
      const hasData =
        oldVal === "multiple"
          ? this.specifications.some(
              (x) => x.name || x.productCode || x.price || x.stock,
            )
          : this.singleSpec.productCode ||
            this.singleSpec.price ||
            this.singleSpec.stock;

      // 沒資料就直接切
      if (!hasData) {
        this.handleSpecTypeChange();
        return;
      }

      // 有資料 → 跳 SW2
      const ok = await showConfirm(
        "切換規格狀態將會移除目前已輸入的資料，是否繼續？",
      );

      if (!ok.isConfirmed) {
        // ❌ 使用者取消 → 切回原狀
        this.isSwitchingSpecType = true;
        this.specType = oldVal;
        this.isSwitchingSpecType = false;
        return;
      }

      // ✅ 使用者確認 → 執行原本切換邏輯
      this.handleSpecTypeChange();
    },

    specifications: {
      deep: true,
      handler() {
        this.hasChanges = true; // ← 只要規格有任何變動就記錄
      },
    },

    singleSpec: {
      deep: true,
      handler() {
        this.hasChanges = true; // ← 單一規格模式也要記錄
      },
    },
  },

  // ✅ 新增：computed計算屬性
  computed: {
    canViewCostPrice() {
      return this.userRole === "平台總管" || this.userRole === "工程師";
    },
  },

  methods: {
    initializeData() {
      // 此段程式碼是控制回填
      this.hasChanges = false;
      this.selectedSpecs = [];

      const p = this.initialData;
      if (!p) return this.resetData();

      // 判斷多規格
      if (p.hasVariant === true && p.variants?.length > 0) {
        this.specType = "multiple";

        this.specifications = p.variants.map((v) => ({
          id: v.Id, // ← 帶回 ID
          productCode: v.Sku || "",
          name: v.VariantName || "",
          price: v.Price || 0,
          costPrice: v.CostPrice || 0, // ✅ 新增：成本价格
          stock: v.Stock || 0,
          meter: v.Meter || false,
        }));

        this.tempSpecifications = JSON.parse(
          JSON.stringify(this.specifications),
        );
      } else {
        this.specType = "single";

        this.singleSpec = {
          productCode: p.Sku || "",
          price: p.price || 0,
          costPrice: p.costPrice || 0, // ✅ 新增：成本价格
          stock: p.stock || 0,
          meter: p.Meter || false,
        };

        this.specifications = [
          {
            productCode: "",
            name: "",
            price: null,
            costPrice: null, // ✅ 新增：成本价格
            stock: null,
          },
        ];
        this.tempSpecifications = [];
      }
    },
    resetData() {
      this.specType = "single";
      this.singleSpec = {
        productCode: "",
        price: null,
        costPrice: null, // ✅ 新增：成本价格
        stock: null,
      };
      this.specifications = [
        {
          productCode: "",
          name: "",
          price: null,
          costPrice: null, // ✅ 新增：成本价格
          stock: null,
        },
      ];
      this.tempSpecifications = [];
      this.selectedSpecs = [];
    },
    handleSpecTypeChange() {
      this.hasChanges = true; // ← 新增
      if (this.specType === "multiple") {
        // 切換到多規格時，恢復暫存的資料
        if (this.tempSpecifications.length > 0) {
          this.specifications = JSON.parse(
            JSON.stringify(this.tempSpecifications),
          );
        } else {
          // 如果沒有暫存資料，初始化一個空規格
          this.specifications = [
            {
              productCode: "",
              name: "",
              price: null,
              stock: null,
            },
          ];
        }
      } else {
        // 切換到單一規格時，暫存多規格資料
        this.tempSpecifications = JSON.parse(
          JSON.stringify(this.specifications),
        );
      }
      // 清空選中狀態
      this.selectedSpecs = [];
    },
    addSpecification() {
      this.specifications.push({
        productCode: "",
        name: "",
        price: null,
        costPrice: null, // ✅ 新增：成本价格
        stock: null,
      });

      this.hasChanges = true; // ← 新增
    },
    removeSpecification(index) {
      if (this.specifications.length > 1) {
        this.specifications.splice(index, 1);
        // 更新選中索引
        this.updateSelectedSpecs(index);
        this.hasChanges = true;
      }
    },
    toggleSpecSelection(index) {
      const selectedIndex = this.selectedSpecs.indexOf(index);
      if (selectedIndex > -1) {
        this.selectedSpecs.splice(selectedIndex, 1);
      } else {
        this.selectedSpecs.push(index);
      }
    },
    async batchDeleteSpecifications() {
      if (this.selectedSpecs.length === 0) return;

      // 至少保留一個規格
      if (this.selectedSpecs.length >= this.specifications.length) {
        showError("至少需要保留一個規格");
        return;
      }

      // 確認刪除
      const ok = await showConfirm(
        `確定要刪除 ${this.selectedSpecs.length} 個規格嗎？`,
      );
      if (!ok.isConfirmed) return;

      // 從大到小排序索引，避免刪除時索引錯亂
      const sortedIndices = [...this.selectedSpecs].sort((a, b) => b - a);

      sortedIndices.forEach((index) => {
        this.specifications.splice(index, 1);
      });

      // 清空選中狀態
      this.selectedSpecs = [];
      this.hasChanges = true; // ← 新增
    },
    updateSelectedSpecs(deletedIndex) {
      // 移除被刪除的索引
      const selectedIndex = this.selectedSpecs.indexOf(deletedIndex);
      if (selectedIndex > -1) {
        this.selectedSpecs.splice(selectedIndex, 1);
      }

      // 更新大於被刪除索引的選中項
      this.selectedSpecs = this.selectedSpecs.map((index) => {
        return index > deletedIndex ? index - 1 : index;
      });
    },
    async handleClose() {
      if (this.hasChanges) {
        const ok = await showConfirm("尚未儲存變更，確定要離開嗎？");
        if (!ok.isConfirmed) return;
      }
      this.$emit("close");
    },
    handleConfirm() {
      const isValid = this.validateSpecifications();
      if (!isValid) {
        // ❌ 不關 modal
        // ❌ 不送資料
        return;
      }

      if (this.specType === "single") {
        // 單規格模式
        const result = {
          productId: this.initialData.productId, // 必填
          hasVariant: false, // 單規格
          variants: [],
        };
        this.$emit("confirm", result);
      } else {
        // 多規格模式
        const result = {
          productId: this.initialData.productId, // 必填
          hasVariant: true,
          variants: this.specifications.map((x) => ({
            id: x.id, // ← 若有 ID 就回傳
            sku: x.productCode,
            variantName: x.name,
            price: x.price,
            costPrice: x.costPrice || 0, // ✅ 新增：成本价格
            stock: x.stock,
            meter: x.meter || false, // ← 要新增 meter
            isActive: true,
          })),
        };
        this.$emit("confirm", result);
      }
      this.hasChanges = false; // ← 這裡要加
      this.$emit("close");
    },

    validateSpecifications() {
      // 🔹 單一規格驗證
      if (this.specType === "single") {
        // ✅ 單一規格不需要驗證，因為資料來自商品本身
        return true;
      }

      // 🔹 多規格驗證
      for (let i = 0; i < this.specifications.length; i++) {
        const spec = this.specifications[i];
        const index = i + 1;

        if (!spec.name || spec.name.trim() === "") {
          showError(`請填寫【規格 ${index}】的規格名稱`);
          return false;
        }

        if (!spec.productCode || spec.productCode.trim() === "") {
          showError(`請填寫【規格 ${index}】的商品貨號`);
          return false;
        }

        if (spec.price === null || spec.price === "" || spec.price < 0) {
          showError(`請填寫【規格 ${index}】的正確售價`);
          return false;
        }

        if (spec.stock === null || spec.stock === "" || spec.stock < 0) {
          showError(`請填寫【規格 ${index}】的正確庫存`);
          return false;
        }
      }

      return true;
    },
    handlePriceInput(spec) {
      // 🟢 保護避免負數
      if (spec.price < 0) {
        spec.price = 0; // ← 直接修正為 0
        showError("售價不能小於 0"); // sweetalert2
      }
      this.hasChanges = true; // ← 新增
    },

    // ✅ 新增：返回上一步
    handleBack() {
      this.$emit("back");
      this.$emit("close");
    },
  },
};
